import { useMemo, useEffect } from 'react'
import { rucksack, theme, Box } from '@black-bear-energy/rucksack'
import {
  HomeRounded,
  SolarPowerRounded,
  MapsHomeWorkRounded,
  SupervisorAccountRounded,
  TopicRounded,
  AddLocationAltRounded,
  EmojiEventsRounded,
  MapRounded,
  HistoryRounded,
} from '@mui/icons-material'
import {
  isBBEUserOrSuperAdmin,
  isAdminOrBBEUserOrSuperAdmin,
  isChannelPartnerUser,
  useAuthInfo,
  useImpersonatingUserOrUser,
} from '../../state/modules/auth'
import { enableBidHistory } from '../../featureFlags'
import { useLocalStorageQuery } from '../../utils/useLocalStorageQuery'
import {
  NAV_TRANSITION,
  NAV_VISIBILITY_KEY,
  NAV_WIDTH_EXPANDED,
  NAV_WIDTH_COLLAPSED,
  HEADER_HEIGHT,
} from './constants'
import { useNavigation } from './NavigationContext'
import { SideNavigationItem } from './SideNavigationItem'
import { SideNavigationSubItem } from './SideNavigationSubItem'
import { SolarRankingTwoToneIcon } from './SolarRankingTwoToneIcon'

/**
 * Collapsible side navigation panel with role-based item visibility.
 * Nav items available for the user are cached in local storage
 * to avoid a flash of empty nav while the user's role is being fetched.
 * */
export function SideNavigation() {
  const { expanded } = useNavigation()
  const impersonatingUserOrUser = useImpersonatingUserOrUser()

  const {
    isLoading,
    settings: { enableProjectsPage, enableSitesPage },
  } = useAuthInfo()

  const isNavigationLoading = isLoading || !impersonatingUserOrUser

  const [cachedVisibility, setCachedVisibility] = useLocalStorageQuery<
    NavVisibility | undefined
  >(NAV_VISIBILITY_KEY, isNavVisibility, undefined)

  const freshVisibility = useMemo<NavVisibility | undefined>(() => {
    if (isNavigationLoading) return undefined

    const isBBEOrSuperAdmin = isBBEUserOrSuperAdmin(impersonatingUserOrUser)
    const isChannelPartner = isChannelPartnerUser(impersonatingUserOrUser)
    const showBidHistory = enableBidHistory && isChannelPartner
    const manageLabel = isAdminOrBBEUserOrSuperAdmin(impersonatingUserOrUser)
      ? 'Manage Users'
      : 'Manage Profile'

    return {
      enableProjectsPage,
      enableSitesPage,
      isBBEOrSuperAdmin,
      isChannelPartner,
      showBidHistory,
      manageLabel,
    }
  }, [
    isNavigationLoading,
    impersonatingUserOrUser,
    enableProjectsPage,
    enableSitesPage,
  ])

  useEffect(() => {
    if (freshVisibility) setCachedVisibility(freshVisibility)
  }, [freshVisibility, setCachedVisibility])

  const visibility = freshVisibility ?? cachedVisibility

  // A project can be selected once the new Projects table is implemented.
  // See https://github.com/Black-Bear-Energy/black-bear-energy-portal/issues/1464
  const isProjectSelected = false

  return (
    <Box
      component="nav"
      sx={{
        position: 'fixed',
        top: HEADER_HEIGHT,
        left: 0,
        bottom: 0,
        width: expanded ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED,
        background: rucksack.palette.background.navigation,
        overflowX: 'hidden',
        overflowY: 'auto',
        transition: theme.transitions.create('width', NAV_TRANSITION),
        paddingY: 1,
        paddingX: 1.5,
        zIndex: rucksack.zIndex.drawer,
      }}
    >
      {visibility && (
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <SideNavigationItem
            to="/"
            label="Dashboard"
            icon={<HomeRounded />}
            exact
          />
          {(visibility.isBBEOrSuperAdmin || visibility.isChannelPartner) && (
            <SideNavigationItem
              to="/rfps"
              label="RFPs"
              icon={<SolarRankingTwoToneIcon />}
            />
          )}
          {visibility.enableProjectsPage && (
            <SideNavigationItem
              to="/manage/projects"
              label="Projects"
              icon={<SolarPowerRounded />}
            >
              {isProjectSelected && (
                <>
                  <SideNavigationSubItem
                    to="/manage/projects/overview"
                    label="Overview"
                  />
                  <SideNavigationSubItem
                    to="/manage/projects/details"
                    label="Details"
                  />
                </>
              )}
            </SideNavigationItem>
          )}
          {visibility.enableSitesPage && (
            <SideNavigationItem
              to="/sites"
              label="Sites"
              icon={<MapsHomeWorkRounded />}
              exact
            />
          )}
          <SideNavigationItem
            to="/manage/users"
            label={visibility.manageLabel}
            icon={<SupervisorAccountRounded />}
          />
          <SideNavigationItem
            to="/document-exchange"
            label="Document Exchange"
            icon={<TopicRounded />}
          />
          {visibility.isBBEOrSuperAdmin && (
            <SideNavigationItem
              to="/sites/upload"
              label="Upload Properties"
              icon={<AddLocationAltRounded />}
            />
          )}
          {visibility.isBBEOrSuperAdmin && (
            <SideNavigationItem
              to="/leaderboard/upload"
              label="Solar Leaderboard Upload"
              icon={<EmojiEventsRounded />}
            />
          )}
          {visibility.isBBEOrSuperAdmin &&
            process.env.REACT_APP_PROSPECTOR_URL && (
              <SideNavigationItem
                href={process.env.REACT_APP_PROSPECTOR_URL}
                label="Prospector"
                icon={<MapRounded />}
              />
            )}
          {visibility.showBidHistory && (
            <SideNavigationItem
              to="/bid/history"
              label="Bid History"
              icon={<HistoryRounded />}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

/** Cached role-based visibility flags that control which nav items are rendered. */
interface NavVisibility {
  /** Whether the Projects page feature flag is enabled. */
  enableProjectsPage: boolean
  /** Whether the user can access the Sites page. */
  enableSitesPage: boolean
  /** Whether the user is a BBE employee or super admin. */
  isBBEOrSuperAdmin: boolean
  /** Whether the user is a channel partner. */
  isChannelPartner: boolean
  /** Whether the bid history feature is enabled for this user. */
  showBidHistory: boolean
  /** Label for the user management nav item, based on the user's role. */
  manageLabel: 'Manage Users' | 'Manage Profile'
}

function isNavVisibility(raw: unknown): raw is NavVisibility {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
    return false
  const obj = raw as Record<string, unknown>
  return (
    typeof obj.enableProjectsPage === 'boolean' &&
    typeof obj.enableSitesPage === 'boolean' &&
    typeof obj.isBBEOrSuperAdmin === 'boolean' &&
    typeof obj.isChannelPartner === 'boolean' &&
    typeof obj.showBidHistory === 'boolean' &&
    (obj.manageLabel === 'Manage Users' || obj.manageLabel === 'Manage Profile')
  )
}
